import React, { useCallback, useEffect, useRef, useState } from "react";
import { MotiView } from "moti";
import { useFocusEffect, useIsFocused, useNavigation } from "@react-navigation/native";
import CustomSpacing from "../../../components/common/layout/CustomSpacing/CustomSpacing";
import CustomText from "../../../components/common/general/CustomText/CustomText";
import { View } from "react-native";
import { ScrollView } from "react-native-gesture-handler";
import Sentry from "../../../utils/sentry";
import {
  ActivityState,
  AssessmentType,
  useGetAssessmentSubmissionsDetailsQuery,
  useGetCourseActivitiesProgressQuery,
  useResetAiTutorMutation,
} from "@axio/graphql/generated/graphql";
import Skeleton from "react-loading-skeleton";
import AiTutorCardProgressList from "../../../components/lms/AiTutorCardProgressList/AiTutorCardProgressList";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import type { RootStackParamList } from "../../../navigation/navigation.types";
import { sortResourceItemsOfModules } from "../../../utils/sortResourceItemsOfModules";
import { ResourceType, useActiveCourse } from "../../../contexts/ActiveCourseContext";
import AssessmentGradingModalContainer from "../../customCoursework/TeacherGradingUIComponents/AssessmentGradingModalContainer";
import { GradingData } from "../../../components/adminDashboard/GradingCenter/GradingData";
import { useAuth } from "../../../hooks/useAuth";
import { usePreviewMode } from "../../../contexts/PreviewModeContext";

interface AiTutorCardProgressListContainerProps {
  integrationId: string;
  courseId: string;
  moduleId?: string; // Optional - when provided, filter to only show resources from this module
}

const AiTutorCardProgressListContainer = ({
  integrationId,
  courseId,
  moduleId,
}: AiTutorCardProgressListContainerProps) => {
  const [modulesWithResources, setModulesWithResources] = useState([]);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isAssessmentGradingModalVisible, setIsAssessmentGradingModalVisible] = useState(false);
  const [selectedAssessmentSubmissionId, setSelectedAssessmentSubmissionId] = useState(null);

  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();

  const { setActiveResource } = useActiveCourse();

  const { userInfo } = useAuth();
  const { isPreviewMode } = usePreviewMode();
  const [resettingILEId, setResettingILEId] = useState<string | null>(null);

  const [resetAiTutor] = useResetAiTutorMutation();
  const isFocused = useIsFocused();
  const isPollingRef = useRef(false);

  const {
    data: course,
    loading,
    refetch,
    startPolling,
    stopPolling,
  } = useGetCourseActivitiesProgressQuery({
    variables: {
      id: courseId,
    },
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
  });

  const { data: assessmentSubmissionDetails, loading: assessmentSubmissionDetailsLoading } =
    useGetAssessmentSubmissionsDetailsQuery({
      variables: {
        assessmentSubmissionId: selectedAssessmentSubmissionId,
      },
      skip: !selectedAssessmentSubmissionId,
    });

  useFocusEffect(
    useCallback(() => {
      if (!isPollingRef.current) {
        refetch();
      }
    }, [refetch])
  );

  useEffect(() => {
    if (course && !loading && userInfo.userId) {
      if (isInitialLoad) {
        setIsInitialLoad(false);
      }

      // Filter modules if moduleId is provided (from feature branch)
      const rawModules = course?.getCourseActivitiesProgress?.data?.[0]?.modules ?? [];
      const modulesToProcess = moduleId ? rawModules.filter((module) => module.id === moduleId) : rawModules;

      const formattedModules =
        modulesToProcess.map((module) => {
          const aiTutors = module.aiTutors.map((aiTutor) => {
            const aiTutorTotalObjectives =
              aiTutor.objectives && aiTutor.objectives.length > 0 ? aiTutor.objectives.length : 0;
            const aiTutorCompletedObjectives =
              aiTutor.objectives && aiTutor.objectives.length > 0
                ? aiTutor.objectives.filter(
                    (objective) => objective.userHasProgressConnection?.edges?.[0]?.properties?.status === "completed"
                  ).length
                : 0;
            const isComplete = aiTutorCompletedObjectives > 0 && aiTutorCompletedObjectives === aiTutorTotalObjectives;
            const resourceDurationMinutes = (aiTutorTotalObjectives - aiTutorCompletedObjectives) * 10;

            return {
              ...aiTutor,
              resourceType: "aiTutor",
              details: {
                isComplete,
                totalObjectives: aiTutorTotalObjectives,
                completedObjectives: aiTutorCompletedObjectives,
                resourceDurationMinutes,
              },
            };
          });

          const axioResources = module.axioResources.map((resource) => ({
            ...resource,
            resourceType: "axioResource",
            details: {
              title: resource.name,
              description: resource.description,
              videoUrl: resource.videoUrl,
            },
            files: resource.files,
          }));

          const assignments = module.assignments.map((assignment) => {
            const latestSubmission = assignment.submissions?.length
              ? [...assignment.submissions].sort(
                  (a, b) => new Date(b.created).getTime() - new Date(a.created).getTime()
                )[0]
              : null;

            const isPending = latestSubmission && latestSubmission.hasTeacherGraded === false;

            const isComplete = latestSubmission && latestSubmission.hasTeacherGraded === true;

            return {
              ...assignment,
              resourceType: "assignment",
              latestSubmission,
              dueDate: assignment.due_date,
              details: {
                title: assignment.name,
                description: assignment.description,
                videoUrl: assignment.videoUrl,

                // grading state
                isPending,
                isComplete,
                grade: latestSubmission?.grade,
              },
              files: assignment.files,
            };
          });

          const reviews = module.reviews
            .map((review) => {
              const userAiTutors =
                review.hasAITutors?.filter((aiTutor) => aiTutor.reviewForUser?.id === userInfo.userId) || [];
              const userAiTutor = userAiTutors?.sort(
                (a, b) => new Date(b.created || 0).getTime() - new Date(a.created || 0).getTime()
              )?.[0];

              if (!userAiTutor) {
                return null;
              }

              const reviewTotalObjectives =
                userAiTutor.objectives && userAiTutor.objectives.length > 0 ? userAiTutor.objectives.length : 0;

              const reviewCompletedObjectives =
                userAiTutor.objectives && userAiTutor.objectives.length > 0
                  ? userAiTutor.objectives.filter(
                      (objective) => objective.userHasProgressConnection?.edges?.[0]?.properties?.status === "completed"
                    ).length
                  : 0;
              const isComplete = reviewCompletedObjectives > 0 && reviewCompletedObjectives === reviewTotalObjectives;
              const resourceDurationMinutes = (reviewTotalObjectives - reviewCompletedObjectives) * 10;

              return {
                id: review.id,
                title: `Review for ${review.hasReviewFor?.moduleHasAssessment?.title || "Unknown Module"}'s Assessment`,
                resourceType: "review",
                aiTutorIdOfReview: userAiTutor.id,
                details: {
                  isComplete,
                  totalObjectives: reviewTotalObjectives,
                  completedObjectives: reviewCompletedObjectives,
                  resourceDurationMinutes,
                },
              };
            })
            .filter(Boolean);

          // Process projects
          const projects = module.projects.map((project) => ({
            ...project,
            resourceType: "project",
            title: project.name, // Add title field for ActivityCard
            details: {
              title: project.name || "Untitled Project",
              description: project.description || "No description available",
              expectedOutcomes: project.expectedOutcomes,
              scopeDocument: project.scopeDocument,
              deliverableTypes: project.deliverableTypes,
              assessmentCriteria: project.assessmentCriteria,
            },
            files: [],
          }));

          const latestAssessmentSubmission =
            module.assessment?.submissions && module.assessment?.submissions.length > 0
              ? [...module.assessment.submissions].sort((a, b) => {
                  const aDate = new Date(a.created);
                  const bDate = new Date(b.created);
                  return bDate.getTime() - aDate.getTime();
                })[0]
              : null;

          const assessment =
            module?.assessment &&
            (module.assessment.assessmentType === AssessmentType.Mcq || module.assessment.aiTutors?.length > 0)
              ? { ...module.assessment, resourceType: "assessment", latestAssessmentSubmission }
              : null;

          const allResources = [
            ...aiTutors,
            ...axioResources,
            ...assignments,
            ...reviews,
            ...(assessment ? [assessment] : []),
            ...projects,
          ];

          return {
            ...module,
            resources: sortResourceItemsOfModules(allResources, module.resourceOrder),
            latestAssessmentSubmission,
          };
        }) || [];

      setModulesWithResources(formattedModules);
    }
  }, [course, loading, isInitialLoad, userInfo.userId, moduleId]);

  useEffect(() => {
    const hasActiveProcessInResources = modulesWithResources.some(
      (module) =>
        module.latestAssessmentSubmission?.isEvaluationPending === true ||
        module.latestAssessmentSubmission?.status === "RUNNING" ||
        module.resources.some(
          (resource) =>
            (resource.resourceType === "review" && resource.isReviewContentBeingGenerated) ||
            (resource.resourceType === "assignment" && resource.details?.isPending)
        )
    );

    const hasActiveProcessInRawData =
      course?.getCourseActivitiesProgress?.data?.[0]?.modules?.some((module) =>
        module.reviews?.some((review) => {
          return (
            (review as any).generatingReviewAITutorForConnection?.edges?.some(
              (edge: any) => edge.node?.id === userInfo.userId && edge.properties?.isGenerating === true
            ) || false
          );
        })
      ) || false;

    const hasActiveProcess = hasActiveProcessInResources || hasActiveProcessInRawData;
    const shouldPoll = isFocused && hasActiveProcess;

    if (shouldPoll && !isPollingRef.current) {
      startPolling(2000);
      isPollingRef.current = true;
    } else if (!shouldPoll && isPollingRef.current) {
      stopPolling();
      isPollingRef.current = false;
    }
  }, [modulesWithResources, course, userInfo.userId, isFocused, startPolling, stopPolling]);

  useEffect(() => {
    return () => {
      stopPolling();
      isPollingRef.current = false;
    };
  }, [stopPolling]);

  const handleResourcePress = (id, resourceType, aiTutorIdOfReview = null, assessmentSubmissionId = null) => {
    if (resourceType === "aiTutor") {
      setActiveResource({ resourceType: ResourceType.ACTIVITY_INTERACTIVE_LEARNING_EXPERIENCE, resourceId: id });
      navigation.navigate("Custom Course AITutor", {
        resource_type: "aiTutor",
        resource_id: id,
        course_id: courseId,
        integration_id: integrationId,
        module_id: moduleId,
      });
    } else if (resourceType === "review") {
      setActiveResource({ resourceType: ResourceType.ACTIVITY_REVIEW, resourceId: aiTutorIdOfReview });
      navigation.navigate("Custom Course AITutor", {
        resource_type: "aiTutor",
        resource_id: aiTutorIdOfReview,
        course_id: courseId,
        integration_id: integrationId,
        module_id: moduleId,
      });
    } else if (resourceType === "assignment") {
      setActiveResource({ resourceType: ResourceType.ACTIVITY_ASSIGNMENT, resourceId: id });
      navigation.navigate("Custom Assignment Student", {
        resource_type: "assignment",
        resource_id: id,
        course_id: courseId,
        integration_id: integrationId,
        module_id: moduleId,
      });
    } else if (resourceType === "assessment") {
      setActiveResource({ resourceType: ResourceType.ACTIVITY_ASSESSMENT, resourceId: id });
      setSelectedAssessmentSubmissionId(assessmentSubmissionId);
      setIsAssessmentGradingModalVisible(true);
    } else if (resourceType === "project") {
      setActiveResource({ resourceType: ResourceType.ACTIVITY_PROJECT, resourceId: id });
      navigation.navigate("Project", {
        id: id,
        courseId: courseId,
        integrationId: integrationId,
      });
    } else {
      setActiveResource({ resourceType: ResourceType.ACTIVITY_RESOURCE, resourceId: id });
      const resource = modulesWithResources
        .flatMap((module) => module.resources)
        .find((resource) => resource.id === id);

      if (resource) {
        const params = {
          resource_type: resourceType,
          resource_id: resource.id,
          course_id: courseId,
          integration_id: integrationId,
          module_id: moduleId,
        };

        navigation.navigate("Custom Resource Student", params);
      } else {
        Sentry.captureException(`Resource with id ${id} not found.`);
      }
    }
  };

  const handleOnHoverIn = (id) => {
    setModulesWithResources((prev) =>
      prev.map((module) => ({
        ...module,
        resources: module.resources.map((resource) =>
          resource.id === id ? { ...resource, isHovered: true } : resource
        ),
      }))
    );
  };

  const handleOnHoverOut = (id) => {
    setModulesWithResources((prev) =>
      prev.map((module) => ({
        ...module,
        resources: module.resources.map((resource) =>
          resource.id === id ? { ...resource, isHovered: false } : resource
        ),
      }))
    );
  };

  const handleResetILE = useCallback(
    async (aiTutorId: string) => {
      if (!userInfo.userId) {
        return;
      }

      setResettingILEId(aiTutorId);
      try {
        const result = await resetAiTutor({
          variables: {
            input: {
              id: aiTutorId,
              userId: userInfo.userId,
            },
          },
        });

        if (result.data?.resetAITutor?.success) {
          await refetch();
        }
      } catch (error) {
        Sentry.captureException(error);
      } finally {
        setResettingILEId(null);
      }
    },
    [resetAiTutor, userInfo.userId, refetch]
  );

  const renderContent = () => {
    if (loading && isInitialLoad) {
      return (
        <ScrollView style={{ flex: 1 }} tabIndex={0}>
          {[1, 2, 3].map((_, idx) => (
            <View key={idx}>
              <CustomSpacing type="vertical" size="l" />
              <Skeleton height={18} width={60} />
              <CustomSpacing type="vertical" size="s" />
              <Skeleton height={32} width={200} />
              <CustomSpacing type="vertical" size="l" />
              {[1, 2, 3].map((__, idx2) => (
                <Skeleton height={80} style={{ marginBottom: 10 }} key={idx2} />
              ))}
            </View>
          ))}
        </ScrollView>
      );
    }
    return (
      <>
        <ScrollView style={{ flex: 1 }} tabIndex={0}>
          {modulesWithResources.map((module) => (
            <View key={module.id}>
              {/* Only show module title if we're showing multiple modules (courseId mode) */}
              {!moduleId && (
                <>
                  <CustomSpacing type="vertical" size="l" />
                  <CustomText
                    text={"MODULE"}
                    size="m"
                    style={{
                      fontFamily: "Inter",
                      fontSize: 12,
                      lineHeight: 18,
                      letterSpacing: "0.06",
                      color: "#667085",
                    }}
                    useTranslationText={false}
                  />
                  <CustomText
                    text={module.title}
                    size="xs"
                    textType="display"
                    useTranslationText={false}
                    weight="ultraBold"
                  />
                  <CustomSpacing type="vertical" size="l" />
                </>
              )}
              <AiTutorCardProgressList
                items={(module.resources ?? []).filter((item) => item.activityState !== ActivityState.Draft)}
                onPress={(id, aiTutorIdOfReview, assessmentSubmissionId) => {
                  const resource = module.resources.find((r) => r.id === id);
                  handleResourcePress(id, resource?.resourceType, aiTutorIdOfReview, assessmentSubmissionId);
                }}
                onHoverIn={handleOnHoverIn}
                onHoverOut={handleOnHoverOut}
                isPreviewMode={isPreviewMode}
                onResetILE={handleResetILE}
                resettingILEId={resettingILEId}
              />
            </View>
          ))}
        </ScrollView>
        <AssessmentGradingModalContainer
          isVisible={isAssessmentGradingModalVisible}
          onClose={() => setIsAssessmentGradingModalVisible(false)}
          isLoading={assessmentSubmissionDetailsLoading}
          gradingData={{
            ...(assessmentSubmissionDetails?.getAssessmentSubmissionDetails?.data as unknown as GradingData),
            type: "Assessment",
          }}
          canEditScore={false}
        />
      </>
    );
  };

  return (
    <MotiView
      from={{ opacity: 0, translateY: 20 }}
      animate={{ opacity: 1, translateY: 0 }}
      delay={moduleId ? 0 : 1500}
      transition={{
        opacity: { type: "timing", duration: 600 },
        translateY: { type: "timing", duration: 600 },
      }}
      style={{ flex: 1 }}
      aria-live="polite">
      {renderContent()}
    </MotiView>
  );
};

export default AiTutorCardProgressListContainer;
